import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, TrendingUp, Users, Calendar } from "lucide-react";

interface InfluencerData {
  name: string;
  position: string;
  profileUrl: string;
  followers: number;
  avgEngagement: number;
  postingFrequency: string;
  influenceScore: number;
  reachMultiplier: number;
  topPost?: {
    text: string;
    engagement: number;
    publishedAt: string;
  };
}

interface InfluenceLeaderboardProps {
  topEmployees: InfluencerData[];
}

export const InfluenceLeaderboard = ({ topEmployees }: InfluenceLeaderboardProps) => {
  if (!topEmployees || topEmployees.length === 0) {
    return null;
  }

  const getMedalIcon = (index: number) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return `${index + 1}.`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-success";
    if (score >= 60) return "text-warning";
    return "text-muted-foreground";
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          <CardTitle>Top Influencers</CardTitle>
        </div>
        <CardDescription>
          Najbardziej wpływowi pracownicy na LinkedIn
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {topEmployees.map((employee, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="text-2xl font-bold min-w-[40px] text-center">
                {getMedalIcon(index)}
              </div>
              
              <div className="flex-1 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <a
                      href={employee.profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-lg hover:text-primary transition-colors"
                    >
                      {employee.name}
                    </a>
                    <p className="text-sm text-muted-foreground">{employee.position}</p>
                  </div>
                  <Badge variant="outline" className={getScoreColor(employee.influenceScore)}>
                    {employee.influenceScore} pts
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {employee.followers.toLocaleString()} followers
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {employee.avgEngagement} avg engagement
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{employee.postingFrequency}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <span className="text-primary font-semibold">
                      {employee.reachMultiplier}x
                    </span>
                    <span className="text-muted-foreground text-xs">reach</span>
                  </div>
                </div>

                {employee.topPost && (
                  <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                    <p className="text-muted-foreground mb-1">Top post:</p>
                    <p className="line-clamp-2">{employee.topPost.text}</p>
                    <p className="text-muted-foreground mt-1">
                      {employee.topPost.engagement} engagement
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
